import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Check, Trash2, Mail, Phone, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDiagnostics = () => {
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchDiagnostics();
  }, []);

  const fetchDiagnostics = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/diagnostics`, { withCredentials: true });
      setDiagnostics(data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const markContacted = async (id) => {
    try {
      await axios.put(`${API}/diagnostics/${id}/contacted`, {}, { withCredentials: true });
      toast.success('Marqué comme contacté');
      fetchDiagnostics();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce diagnostic ?')) return;
    try {
      await axios.delete(`${API}/diagnostics/${id}`, { withCredentials: true });
      toast.success('Supprimé');
      fetchDiagnostics();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success(`Email copié : ${email}`);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminLayout title="Diagnostics Olfactifs">
      <p className="text-[#332724] font-light mb-6">
        Tous les diagnostics olfactifs réalisés sur votre site. Contactez vos clients pour leur
        proposer une consultation en boutique.
      </p>

      {loading ? (
        <div>Chargement...</div>
      ) : diagnostics.length === 0 ? (
        <div className="bg-white p-12 rounded-lg border border-gold text-center">
          <p className="text-[#332724] font-light">Aucun diagnostic pour le moment.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {diagnostics.map((d) => (
            <div
              key={d.id}
              className={`bg-white p-6 rounded-lg border ${
                d.contacted ? 'border-green-200' : 'border-[#B8922A]/30'
              }`}
              data-testid={`diagnostic-${d.id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-[#5C2018]">{d.name}</h3>
                    {d.contacted ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        ✓ Contacté
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                        À contacter
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => copyEmail(d.email)}
                    className="flex items-center gap-2 text-sm text-[#1E7A6A] hover:underline mb-1"
                  >
                    <Mail size={14} />
                    <span>{d.email}</span>
                    <Copy size={12} />
                  </button>
                  <p className="text-xs text-[#332724]/70">{formatDate(d.created_at)}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  {!d.contacted && (
                    <button
                      onClick={() => markContacted(d.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded"
                      title="Marquer comme contacté"
                      data-testid={`mark-contacted-${d.id}`}
                    >
                      <Check size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(d.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Supprimer"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <button
                onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                className="text-sm text-[#B8922A] hover:underline mb-2"
              >
                {expanded === d.id ? '▲ Masquer les détails' : '▼ Voir préférences et recommandations'}
              </button>

              {expanded === d.id && (
                <div className="mt-3 pt-3 border-t border-[#B8922A]/20 space-y-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-2">
                      Préférences
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(d.preferences).map(([key, value]) => (
                        <div key={key} className="text-sm">
                          <span className="text-[#332724]/70 capitalize">{key}: </span>
                          <span className="text-[#332724] font-medium">
                            {Array.isArray(value) ? value.join(', ') : value || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-2">
                      Recommandations envoyées
                    </p>
                    <pre className="text-sm text-[#332724] whitespace-pre-wrap font-light bg-[#FDFAF4] p-3 rounded">
                      {d.results}
                    </pre>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <a
                      href={`mailto:${d.email}?subject=Votre diagnostic olfactif - Harmony Parfumerie&body=Bonjour ${d.name},%0D%0A%0D%0AMerci d'avoir réalisé votre diagnostic olfactif chez Harmony.%0D%0A%0D%0A`}
                      className="btn-primary px-4 py-2 rounded-full text-sm inline-flex items-center gap-2"
                      data-testid={`email-${d.id}`}
                    >
                      <Mail size={14} />
                      Envoyer un email
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDiagnostics;
